import { Injectable } from '@angular/core';
import { createRemoteJWKSet, jwtVerify, JWTPayload } from 'jose';
import { environment } from '../../../environments/environment';

export type JwtVerificationResult =
  | { status: 'valid'; payload: JWTPayload }
  | { status: 'invalid' } // definitively bad token (bad signature, expired, malformed) - MUST block
  | { status: 'unknown' }; // verification could not be COMPLETED at all, OR the signature is valid but audience/issuer didn't match expectations - do NOT block

// Verifies an Azure AD-issued JWT's cryptographic signature (and expiry) against the
// tenant's published JWKS public keys.
//
// This is REAL signature verification, unlike `jwt-decode` used elsewhere in this app, which
// only reads the payload and cannot detect a tampered/forged token - it will happily "decode"
// a token with a completely invalid signature since it never checks the signature at all.
//
// IMPORTANT design note (REVERTED 2026-09-30 - see incident note below): only signature +
// exp/nbf are enforced as hard failures by jose itself. Audience/issuer are checked
// separately, immediately AFTER signature verification succeeds, and a MISMATCH ONLY EVER
// DOWNGRADES TO 'unknown' (never blocks) - hasExactAudience()/hasExactIssuer() exist and are
// still called for rich diagnostic logging on every mismatch, but their result is NOT used to
// reject a token. Do NOT change a mismatch here back into a hard 'invalid' failure without
// FIRST confirming the real aud/iss claim format via the console logs below from actual
// production tokens (this exact mistake was made twice now - see incident note).
//
// INCIDENT (2026-09-30): briefly tightened this to a hard 'invalid' rejection on ANY aud/iss
// mismatch (AppSec Finding #7 "closure") using a GUESSED exact issuer format (v1
// sts.windows.net / v2 login.microsoftonline.com/.../v2.0), without confirming it against a
// real production token first - reintroducing the EXACT failure mode the v2 fix (see
// jwt-verification.md memory) already documents from an earlier incident. Combined with
// auth.interceptor.ts blocking on 'invalid', this caused real users to be bounced to
// /unauthorized -> cleared session -> redirected to login -> silently re-authenticated by
// Azure SSO -> failed again, i.e. a continuous reload loop. Reverted the ENFORCEMENT (back
// to fail-open/'unknown') but kept the exact-match helper functions + diagnostic logging so
// the NEXT occurrence captures the real aud/iss values needed to fix this properly.
@Injectable({
  providedIn: 'root'
})
export class JwtVerificationService {
  // createRemoteJWKSet caches the fetched public keys internally (with automatic re-fetch on
  // key rotation), so repeated verify calls do NOT refetch the JWKS endpoint every time -
  // only on cache miss/rotation.
  private jwks?: ReturnType<typeof createRemoteJWKSet>;

  // Memoizes the in-flight/resolved verification Promise per token string. On a normal page
  // load, many API calls fire concurrently and all carry the SAME bearer token - verifying
  // it separately for each one is not just wasteful, it was observed to occasionally return
  // an inconsistent ('invalid') result for exactly one of several truly-concurrent calls
  // (a transient race further down in the JWKS key-fetch/import path). Caching by token
  // string means every concurrent caller for the same token awaits the exact same single
  // verification, so the outcome is always consistent no matter how many callers there are.
  private verificationCache = new Map<string, Promise<JwtVerificationResult>>();

  private getJwks(): ReturnType<typeof createRemoteJWKSet> {
    if (!this.jwks) {
      const jwksUri = environment.tokenURL.replace('/oauth2/v2.0/token', '/discovery/v2.0/keys');
      this.jwks = createRemoteJWKSet(new URL(jwksUri));
    }
    return this.jwks;
  }

  // Verifies the token's signature + exp/nbf, then sanity-checks audience/issuer.
  //
  // Returns:
  //  - 'valid'   - signature checks out (not expired), and audience/issuer look right.
  //  - 'invalid' - the token is DEFINITIVELY bad: expired, bad signature, or malformed.
  //                Callers MUST treat this as untrusted and block.
  //  - 'unknown' - either verification could not be completed (e.g. the JWKS endpoint could
  //                not be reached) OR the signature is valid but the audience/issuer didn't
  //                match our (possibly imperfect) expectations. Deliberately NOT treated as
  //                invalid in either case: failing closed on an assumption we're not 100%
  //                sure about would risk locking every signed-in user out of the app, which
  //                is worse than skipping a check we couldn't confidently perform.
  // `context` is just a human-readable label identifying the caller (e.g. the request's
  // "METHOD url", or "login"/"bootstrap" for the two app.component.ts checkpoints) - it is
  // NOT part of the cache key, it only flows into the console logs below so a failure can be
  // traced back to exactly which caller triggered it.
  async verifyToken(token: string | null | undefined, context = 'unknown caller'): Promise<JwtVerificationResult> {
    if (!token) {
      return { status: 'invalid' };
    }

    const cached = this.verificationCache.get(token);
    if (cached) {
      // console.log(`[JwtVerificationService] [${context}] Reusing cached verification result for this token (avoids re-verifying an already-checked token).`);
      return cached;
    }

    // console.log(`[JwtVerificationService] [${context}] No cached result - running a fresh verification for this token.`);
    const verificationPromise = this.performVerification(token, false, context);
    this.verificationCache.set(token, verificationPromise);

    const result = await verificationPromise;

    // Don't permanently cache anything other than a confirmed 'valid' result. Caching
    // 'invalid' was found to permanently "stick" a token as bad for the rest of the
    // session even when the failure was actually a one-off transient glitch (e.g. a JWKS
    // race) - every later call for that same token string would then keep returning the
    // same cached 'invalid' forever, with no way to recover short of a full re-login. Only
    // a fresh, real re-check should ever be allowed to produce 'invalid' again.
    if (result.status !== 'valid') {
      this.verificationCache.delete(token);
    }

    // Defensive cap so the cache can't grow unbounded across many token rotations over a
    // long browser session.
    if (this.verificationCache.size > 5) {
      const oldestKey = this.verificationCache.keys().next().value;
      if (oldestKey) {
        this.verificationCache.delete(oldestKey);
      }
    }

    return result;
  }

  private async performVerification(token: string, isRetry = false, context = 'unknown caller'): Promise<JwtVerificationResult> {
    const DEFINITIVELY_INVALID_CODES = new Set([
      'ERR_JWT_EXPIRED',
      'ERR_JWS_SIGNATURE_VERIFICATION_FAILED',
      'ERR_JWS_INVALID',
      'ERR_JWT_INVALID'
    ]);

    try {
      // Deliberately NOT passing `audience`/`issuer` options into jose's jwtVerify() itself -
      // a v1 design attempt did this and jose throws (treating ANY mismatch, including a
      // getting-the-format-slightly-wrong bug on our side, as indistinguishable from a real
      // attack) before we get a chance to log which exact value it saw vs expected. Only
      // signature + exp/nbf are enforced by jose itself; audience/issuer are checked
      // separately, right below, with full diagnostics on a mismatch.
      const { payload } = await jwtVerify(token, this.getJwks());

      // SECURITY (reverted 2026-09-30 - see top-of-file incident note): a mismatch here is
      // logged in full (so the NEXT occurrence gives us real data to fix this properly) but
      // deliberately does NOT reject the token - only downgrades to 'unknown'. Enumerates
      // every legitimate known-good form (v1 `sts.windows.net` / v2
      // `login.microsoftonline.com/.../v2.0` issuers, plain clientId / `api://{clientId}`
      // audiences), but since this exact format was never confirmed against a real
      // production token, treating a mismatch as definitively 'invalid' risks locking out
      // every real user on a format assumption we got wrong - already happened once before
      // (see jwt-verification.md's v2 history) and again just now.
      if (!this.hasExactAudience(payload) || !this.hasExactIssuer(payload)) {
        console.warn(`[JwtVerificationService] [${context}] audience/issuer did not match the assumed exact format (NOT blocking - treating as unknown). If you're investigating a login issue, these are the values to compare against Azure AD's real token:`, {
          aud: payload.aud,
          iss: payload.iss,
          expectedAudiences: this.getExpectedAudiences(),
          expectedIssuers: this.getExpectedIssuers()
        });
        return { status: 'unknown' };
      }

      // console.log(`[JwtVerificationService] [${context}] Verification succeeded (signature + claims all check out).`, { isRetry, exp: payload.exp });
      return { status: 'valid', payload };
    } catch (error: any) {
      // A signature failure can be caused by a stale/partially-populated JWKS key cache
      // rather than a genuinely bad token (observed: the SAME token intermittently failed
      // verification exactly once among many concurrent, otherwise-successful
      // verifications). Before concluding the token is truly invalid, force a fresh JWKS
      // fetch and retry exactly once - only if that ALSO fails do we treat it as invalid.
      if (!isRetry && error?.code === 'ERR_JWS_SIGNATURE_VERIFICATION_FAILED') {
        // console.warn(`[JwtVerificationService] [${context}] Signature check failed on first attempt - refreshing JWKS and retrying once before concluding invalid.`);
        this.jwks = undefined;
        return this.performVerification(token, true, context);
      }

      if (error?.code && DEFINITIVELY_INVALID_CODES.has(error.code)) {
        // console.error(`[JwtVerificationService] [${context}] FINAL: token failed verification after` + (isRetry ? ' retry' : ' first attempt') + ':', error.code, error.message);
        // console.error(`[JwtVerificationService] [${context}] WHY it likely failed:`, this.explainFailure(token, error));
        return { status: 'invalid' };
      }
      // console.warn(`[JwtVerificationService] [${context}] Could not complete token verification (treating as unknown, not blocking):`, error?.code, error);
      return { status: 'unknown' };
    }
  }

  // Builds a diagnostic explanation for a DEFINITIVE verification failure, since the raw
  // jose error code/message alone (e.g. "ERR_JWS_SIGNATURE_VERIFICATION_FAILED: signature
  // verification failed") doesn't say WHY - it just means "the cryptographic check did not
  // pass", without indicating whether that's because the token expired, the wrong JWKS
  // endpoint/tenant is configured, the token's signing key isn't in our JWKS yet (key
  // rotation), the local clock is skewed, or the token was genuinely tampered with. This
  // decodes the token WITHOUT verifying it (jwt-decode, same as used elsewhere in the app)
  // purely to surface those clues in the logs - it never affects the actual trust decision.


  // Exact (non-substring) audience check: the aud claim must equal EXACTLY the configured
  // clientId, or exactly the `api://{clientId}` form - never merely CONTAIN either string
  // (Finding #7: a substring/prefix check can be satisfied by an attacker-crafted value that
  // happens to contain the right text without actually BEING it).
  // Fails closed when clientId isn't configured - clientId is always present in every
  // deployed config.json (the login flow itself depends on it), so this only ever triggers
  // on a genuine configuration failure, never as a false positive against a real user's token.
  //
  // Public: also reused by app.component.ts's validateJwtClaims() (AppSec High Finding #7 /
  // #9-style de-duplication) so there's a SINGLE source of truth for what counts as a
  // legitimate audience/issuer, instead of two independently-drifting implementations.
  getExpectedAudiences(): string[] {
    const clientId = environment.clientId;
    return clientId ? [clientId, `api://${clientId}`] : [];
  }

  hasExactAudience(payload: JWTPayload): boolean {
    const expected = this.getExpectedAudiences();
    if (expected.length === 0) {
      return false;
    }
    const audValues = Array.isArray(payload.aud) ? payload.aud : [payload.aud];
    return audValues.some(value => typeof value === 'string' && expected.includes(value));
  }

  // Exact (non-substring) issuer check: the iss claim must equal EXACTLY one of this
  // tenant's two legitimate Azure AD issuer forms - the v2.0 endpoint
  // (`https://login.microsoftonline.com/{tenantId}/v2.0`) or the v1.0 endpoint
  // (`https://sts.windows.net/{tenantId}/`) - never merely CONTAIN the hostname/tenant ID
  // as substrings (Finding #7: the prior `.includes(...)` check could be satisfied by a
  // crafted issuer like `https://evil.example/login.microsoftonline.com.attacker/{tenantId}`,
  // which contains both substrings without being either real endpoint - a genuine
  // cross-tenant/token-confusion gap). Trailing slashes/casing are normalized away since
  // those don't affect the security-relevant hostname+tenant binding.
  // Fails closed when the tenant ID can't be parsed out of environment.tokenURL, or the
  // token carries no `iss` claim at all - environment.tokenURL always embeds the tenant ID
  // in this app's deployed configs, so this only ever triggers on a genuine
  // configuration/token failure, never as a false positive against a real user's token.
  //
  // Public: see hasExactAudience()'s doc comment above for why.
  getExpectedIssuers(): string[] {
    const tenantId = environment.tokenURL?.match(/login\.microsoftonline\.com\/([^/]+)\//)?.[1];
    if (!tenantId) {
      return [];
    }
    return [
      this.normalizeIssuer(`https://login.microsoftonline.com/${tenantId}/v2.0`),
      this.normalizeIssuer(`https://sts.windows.net/${tenantId}/`),
    ];
  }

  normalizeIssuer(value: string | undefined | null): string {
    return (value ?? '').trim().toLowerCase().replace(/\/+$/, '');
  }

  hasExactIssuer(payload: JWTPayload): boolean {
    const expectedIssuers = this.getExpectedIssuers();
    if (expectedIssuers.length === 0 || !payload.iss) {
      return false;
    }
    return expectedIssuers.includes(this.normalizeIssuer(payload.iss));
  }
}
